import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { LOCAL_STORAGE_KEY } from '../constants/auth';

// Session stored in localStorage.
// isAnonymous = true means the user hasn't linked any social account yet.
// When the backend returns a JWT after OAuth, token replaces the anonymous session.
export interface SocialSession {
  userId: string;
  isAnonymous: boolean;
  token?: string; // JWT from backend (present after backend validates OAuth)
}

export interface AuthState {
  session: SocialSession;
  isReady: boolean; // false only during initial mount
  signOut: () => void;
}

const AuthContext = React.createContext<AuthState>({
  session: { userId: '', isAnonymous: true },
  isReady: false,
  signOut: () => {}
});

const loadOrCreateSession = (): SocialSession => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as SocialSession;
    }
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }

  // No session found — create an anonymous one with a random user-id.
  // In production the backend would sign this UUID as a JWT and return it.
  const newSession: SocialSession = {
    userId: uuidv4(),
    isAnonymous: true
  };
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newSession));
  return newSession;
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [session, setSession] = React.useState<SocialSession>({ userId: '', isAnonymous: true });
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    // Check if the backend redirected back with a validated token (?token=xxx).
    // This happens after a successful OAuth flow (TikTok / Instagram).
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const userId = params.get('userId');

    if (token && userId) {
      const newSession: SocialSession = { userId, isAnonymous: false, token };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newSession));
      setSession(newSession);

      // Clean token params from URL
      if (window.history.pushState) {
        const clean = window.location.protocol + '//' + window.location.host + window.location.pathname;
        window.history.pushState({}, '', clean);
      }
    } else {
      setSession(loadOrCreateSession());
    }

    setIsReady(true);
  }, []);

  const signOut = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    // Create a fresh anonymous session
    const fresh = loadOrCreateSession();
    setSession(fresh);
  };

  return (
    <AuthContext.Provider value={{ session, isReady, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => React.useContext(AuthContext);
